import {
  ensureFreshSession,
  notifyAuthSessionRefreshed,
  refreshAuthSession,
} from './authRefresh';
import {
  clearAuthSession,
  getAccessToken,
  getActiveSession,
} from './authSession';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/**
 * Still an Error, so every `error instanceof Error ? error.message` caller keeps working, but
 * callers that need to branch on a specific failure can read the status and backend error code.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const isApiError = (error: unknown, code: string): boolean =>
  error instanceof ApiError && error.code === code;

const isAuthEndpoint = (path: string): boolean => path.startsWith('/api/v1/auth/');

// Requesting without a session yields a MISSING_TENANT 400, which never triggers the 401 login redirect.
const requireFreshSession = async (): Promise<void> => {
  const session = await ensureFreshSession();
  if (session) {
    return;
  }

  clearAuthSession();
  window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  throw new Error('Session has expired. Please log in again.');
};

export const apiRequest = async <TResponse>(
  path: string,
  init: RequestInit = {},
  allowRefreshRetry = true,
): Promise<TResponse> => {
  if (!isAuthEndpoint(path)) {
    await requireFreshSession();
  }

  const session = getActiveSession();
  const headers = new Headers(init.headers || {});
  const isFormDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData;

  if (!headers.has('Content-Type') && init.body && !isFormDataBody) {
    headers.set('Content-Type', 'application/json');
  }

  const tenantId = session?.tenantId?.trim();
  if (tenantId) {
    headers.set('TenantId', tenantId);
    headers.set('X-Tenant-Id', tenantId);
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  let payload: any = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.status === 401 && allowRefreshRetry && !isAuthEndpoint(path)) {
    try {
      const refreshed = await refreshAuthSession({ tenantId: session?.tenantId });
      if (refreshed) {
        notifyAuthSessionRefreshed(refreshed);
        return apiRequest<TResponse>(path, init, false);
      }
    } catch {
      clearAuthSession();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthSession();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const baseMessage = payload?.error?.message || payload?.message || `Request failed with status ${response.status}`;
    const details = Array.isArray(payload?.error?.details)
      ? payload.error.details
      : Array.isArray(payload?.details)
        ? payload.details
        : [];

    const detailMessage = details
      .map((detail: Record<string, unknown>) => {
        const field = detail?.field
          ?? detail?.Field
          ?? detail?.propertyName
          ?? detail?.PropertyName
          ?? detail?.name
          ?? detail?.Name;
        const message = detail?.issue
          ?? detail?.Issue
          ?? detail?.message
          ?? detail?.Message
          ?? detail?.errorMessage
          ?? detail?.ErrorMessage;
        if (!message || typeof message !== 'string') return null;
        return field && typeof field === 'string' ? `${field}: ${message}` : message;
      })
      .filter(Boolean)
      .join('; ');

    const isGenericValidation = /one or more fields are invalid/i.test(baseMessage);
    const resolvedMessage = detailMessage && isGenericValidation
      ? detailMessage
      : detailMessage
        ? `${baseMessage} ${detailMessage}`
        : baseMessage;

    throw new ApiError(
      resolvedMessage,
      response.status,
      typeof payload?.error?.code === 'string' ? payload.error.code : null,
    );
  }

  return payload as TResponse;
};

export const apiRequestRaw = async (
  path: string,
  init: RequestInit = {},
  allowRefreshRetry = true,
): Promise<Response> => {
  if (!isAuthEndpoint(path)) {
    await requireFreshSession();
  }

  const session = getActiveSession();
  const headers = new Headers(init.headers || {});
  const isFormDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData;

  if (!headers.has('Content-Type') && init.body && !isFormDataBody) {
    headers.set('Content-Type', 'application/json');
  }

  const tenantId = session?.tenantId?.trim();
  if (tenantId) {
    headers.set('TenantId', tenantId);
    headers.set('X-Tenant-Id', tenantId);
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (response.status === 401 && allowRefreshRetry && !isAuthEndpoint(path)) {
    try {
      const refreshed = await refreshAuthSession({ tenantId: session?.tenantId });
      if (refreshed) {
        notifyAuthSessionRefreshed(refreshed);
        return apiRequestRaw(path, init, false);
      }
    } catch {
      clearAuthSession();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthSession();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    const baseMessage = payload?.error?.message || payload?.message || `Request failed with status ${response.status}`;
    throw new ApiError(
      baseMessage,
      response.status,
      typeof payload?.error?.code === 'string' ? payload.error.code : null,
    );
  }

  return response;
};

export default apiRequest;
